/** programFields: Employee Team */
export const EMPLOYEE_TEAM_FIELD_ID =
  "2e9f7112-1775-4b69-b55e-3134333bfdf4";

export const APP_DEFAULT_TZ = "Europe/Tirane";

/** IANA for Prishtina / Kosovo (no Europe/Prishtina in IANA). */
export const PRISHTINA_TZ = "Europe/Belgrade";

/** Company-wide default for pickers, list windows, and report ranges. */
export function getCompanyTimezone() {
  return APP_DEFAULT_TZ;
}

/** Curated list for Settings picker — not an allowlist of team names. */
export const COMMON_TIMEZONES = [
  { value: "Europe/Tirane", label: "Europe/Tirane (Tirana)" },
  { value: "Europe/Belgrade", label: "Europe/Belgrade (Prishtina)" },
  { value: "Europe/Skopje", label: "Europe/Skopje" },
  { value: "Europe/Athens", label: "Europe/Athens" },
  { value: "Europe/Rome", label: "Europe/Rome" },
  { value: "Europe/Berlin", label: "Europe/Berlin" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Paris", label: "Europe/Paris" },
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "America/New_York" },
];

/**
 * Guess IANA for a legacy team name string.
 * @param {string} name
 */
export function seedTimezoneForTeamName(name) {
  const n = String(name || "")
    .trim()
    .toLowerCase();
  if (!n) return APP_DEFAULT_TZ;
  if (n.includes("prishtin") || n.includes("pristin") || n.includes("kosov")) {
    return PRISHTINA_TZ;
  }
  if (n.includes("tiran") || n.includes("albania") || n.includes("shqip")) {
    return APP_DEFAULT_TZ;
  }
  return APP_DEFAULT_TZ;
}

/**
 * Normalize one team option to { name, timezone }.
 * @param {string|{name?: string, timezone?: string}} option
 */
export function normalizeTeamOption(option) {
  if (option == null) return null;
  if (typeof option === "string") {
    const name = option.trim();
    if (!name) return null;
    return { name, timezone: seedTimezoneForTeamName(name) };
  }
  if (typeof option === "object") {
    const name = String(option.name ?? option.label ?? "").trim();
    if (!name) return null;
    const timezone =
      typeof option.timezone === "string" && option.timezone.trim()
        ? option.timezone.trim()
        : seedTimezoneForTeamName(name);
    return { name, timezone };
  }
  return null;
}

/**
 * @param {unknown} fieldOptions
 * @returns {{ name: string, timezone: string }[]}
 */
export function normalizeTeamOptions(fieldOptions) {
  if (!Array.isArray(fieldOptions)) return [];
  return fieldOptions.map(normalizeTeamOption).filter(Boolean);
}

/**
 * @param {Array|undefined} programFields
 */
export function findEmployeeTeamField(programFields) {
  if (!Array.isArray(programFields)) return null;
  return (
    programFields.find((f) => f?.fieldId === EMPLOYEE_TEAM_FIELD_ID) ||
    programFields.find((f) => f?.fieldName === "Employee Team") ||
    null
  );
}

/**
 * Resolve IANA timezone for an employee team name.
 * @param {string|undefined|null} employeeTeam
 * @param {Array|undefined} programFields
 * @param {string} [companyDefault]
 */
export function resolveTeamTimezone(
  employeeTeam,
  programFields,
  companyDefault = APP_DEFAULT_TZ
) {
  const fallback =
    typeof companyDefault === "string" && companyDefault.trim()
      ? companyDefault.trim()
      : APP_DEFAULT_TZ;

  const teamName = String(employeeTeam || "").trim();
  if (!teamName) return fallback;

  const field = findEmployeeTeamField(programFields);
  const options = normalizeTeamOptions(field?.fieldOptions);
  const match = options.find(
    (o) => o.name.toLowerCase() === teamName.toLowerCase()
  );
  if (match?.timezone) return match.timezone;

  return seedTimezoneForTeamName(teamName) || fallback;
}

/**
 * Prefer clocking snapshot, else resolve from employee team.
 * @param {{ timezone?: string, employeeTeam?: string }|null|undefined} clocking
 * @param {string|undefined|null} employeeTeam
 * @param {Array|undefined} programFields
 */
export function resolveClockingTimezone(
  clocking,
  employeeTeam,
  programFields
) {
  if (typeof clocking?.timezone === "string" && clocking.timezone.trim()) {
    return clocking.timezone.trim();
  }
  return resolveTeamTimezone(
    employeeTeam || clocking?.employeeTeam,
    programFields
  );
}
